import { useState } from 'react';

import useReferralCode from 'v3/hooks/useReferralCode';

import * as S from './referralFieldStyles';

/**
 * Referral code field for the ReviewOffer step.
 *
 * Applying binds the code to the contract flow (see useReferralCode) — validation
 * alone persists nothing.
 *
 * The input stays editable after applying so the code can be changed: re-applying
 * a different one rebinds it on the backend (set_referral reassigns
 * contract_flow.member_referral and recalculates the offer). There is no "remove",
 * because no un-apply endpoint exists — clearing the box could only clear the UI
 * while the code stayed bound.
 */

const c = {
  label: 'Referral or Promo Code',
  placeholder: 'Enter a code',
  apply: 'Apply',
  applying: 'Checking…',
  update: 'Update',
};

interface ReferralFieldProps {
  /** Used as club_id when validating the code. */
  clubId?: string;
  /** Contract flow the code gets bound to. */
  cfUuid?: string;
  /** Lifts the result into leadData, which feeds the offer summary. */
  onApplied?: (code: string, contractFlow: any) => void;
  /** Code arriving from ?referralCode= in the URL, applied on the previous step.
   *  Null when the flow carries no referral — the API sends null, not an absent
   *  key, so a default parameter would not cover it. */
  initialCode?: string | null;
  /** Rate line for a code that is already bound, so it shows on load rather than
   *  only after applying one in this session. */
  initialDescription?: string | null;
}

const ReferralField = ({
  clubId,
  cfUuid,
  onApplied,
  initialCode,
  initialDescription,
}: ReferralFieldProps) => {
  const [code, setCode] = useState(initialCode ?? '');
  const { isApplied, appliedCode, description, error, isChecking, apply } =
    useReferralCode(clubId, cfUuid, initialCode ?? '', initialDescription ?? '');

  const trimmed = code.trim();
  const isUnchanged = isApplied && trimmed.toUpperCase() === appliedCode.toUpperCase();

  const handleApply = async () => {
    const result = await apply(code);
    if (!result) return;

    // Reflect the code that was actually sent, so the field and leadData agree with
    // what the backend now holds instead of the casing that was typed.
    setCode(result.code);
    onApplied?.(result.code, result.contractFlow);
  };

  return (
    <S.FieldGroup>
      <S.Label>{c.label}</S.Label>
      <S.Control>
        <S.ReferralRow>
          <S.ReferralInput
            aria-label={c.label}
            value={code}
            placeholder={c.placeholder}
            /* Green only while the box still holds the code that is bound — editing
               it towards a new one drops back to normal ink. */
            $applied={isUnchanged}
            onChange={(e) => setCode(e.target.value)}
          />
          <S.ApplyButton
            type="button"
            onClick={handleApply}
            disabled={!trimmed || isChecking || isUnchanged}
          >
            {isChecking ? c.applying : isApplied ? c.update : c.apply}
          </S.ApplyButton>
        </S.ReferralRow>

        {/* Just the rate. The code is already in the field above, so repeating it
            here said the same thing twice. */}
        {isApplied && description ? (
          <S.AppliedNote>{description}</S.AppliedNote>
        ) : null}

        {error ? <S.ReferralError>{error}</S.ReferralError> : null}
      </S.Control>
    </S.FieldGroup>
  );
};

export default ReferralField;
